import { useEffect } from "react";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import { showChatBot } from "helpers/hubspot-chat";
import useLocalStorage from "../../hooks/local-storage";
import LinkButton from "../../components/atoms/link-button/LinkButton";
import Button, { ButtonProps } from "../../components/atoms/button/Button";
import { Stack, StackProps, Typography, TypographyProps } from "@mui/material";
import NavCard, { NavCardProps } from "../../components/organisms/nav-card/NavCard";
import CheckBoxes, { CheckBoxesProps } from "../../components/molecules/check-boxes/CheckBoxes";
import GetStartedFlow, { GetStartedFlowProps } from "../../components/organisms/get-started-flow/GetStartedFlow";

export interface GetStartedProducts {
    VAT: boolean;
    RND: boolean;
    CREATIVE: boolean;
}

function GetStarted() {
    const router = useRouter();

    const [products, setProducts] = useLocalStorage<GetStartedProducts | null>("gs-products", null);

    const navCardProps: NavCardProps = {
        onBack() {
            router.back();
        },
    };

    const getStartedFlowProps: GetStartedFlowProps = {
        footer: <LinkButton onClick={() => showChatBot()}>Not sure?</LinkButton>,
    };

    const stackProps: StackProps = {
        sx: {
            gap: 3,
            alignItems: "center",
        },
    };

    const headingProps: TypographyProps = {
        variant: "h3",
        sx: {
            textAlign: "center",
        },
        children: "How can we help you today?",
    };

    const checkBoxesProps: CheckBoxesProps<any> = {
        formik: useFormik({
            initialValues: {
                VAT: false,
                RND: false,
                CREATIVE: false,
            },
            onSubmit(values, { setSubmitting }) {
                setSubmitting(false);
                setProducts(values);

                if (values.VAT) {
                    router.push("/get-started/is-vat-commercial");
                } else if (values.RND || values.CREATIVE) {
                    router.push("/get-started/estimate");
                }
            },
        }),
        options: [
            {
                label: "VAT services & refunds",
                value: "VAT",
            },
            {
                label: "R&D tax credits / RDEC",
                value: "RND",
            },
            {
                label: "Creative industry tax credits",
                value: "CREATIVE",
            },
        ],
    };

    useEffect(() => {
        if (products) {
            checkBoxesProps.formik.setValues(products);
        }
    }, [products]);

    const actionsStackProps: StackProps = {
        sx: {
            gap: 3,
            alignItems: "center",
        },
    };

    const continueButtonProps: ButtonProps = {
        id: "get-started_continue",
        showEndIcon: true,
        children: "Continue",
        loading: checkBoxesProps.formik.isSubmitting,
        disabled: !Object.values(checkBoxesProps.formik.values).some(value => value),
        onClick: checkBoxesProps.formik.submitForm,
    };

    return (
        <GetStartedFlow {...getStartedFlowProps}>
            <NavCard {...navCardProps}>
                <Stack {...stackProps}>
                    <Typography {...headingProps} />
                    <CheckBoxes {...checkBoxesProps} />
                </Stack>
            </NavCard>
            <Stack {...actionsStackProps}>
                <Button {...continueButtonProps} />
            </Stack>
        </GetStartedFlow>
    );
}

export default GetStarted;
